import { FC } from 'react';

import { Flex, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import IconInfo from '@/assets/icons/icon-info.svg?react';
import Table from '@/components/RTable';
import Tooltip from '@/components/RTooltip';
import useCurrency from '@/contexts/useCurrency';
import { SimulationResult } from '@/types/simulations';
import { formatNumberWithCommas } from '@/utils';

import { StyledTable } from './styles';

interface TotalTableDataTypes {
  key: string;
  title: string | JSX.Element;
  beforeStorage: string;
  afterStorage: string;
  storage: string;
}

interface TotalTableProps {
  simulationData: SimulationResult;
}

const TotalTable: FC<TotalTableProps> = ({ simulationData }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { currencySymbol } = useCurrency();
  const totalDataTable: TotalTableDataTypes[] = [
    {
      key: '1',
      title: (
        <Flex
          justify="space-between"
          align="center"
          style={{
            padding: '17px 24px',
            backgroundColor: theme.colors.blueLightest,
            borderTop: '3px solid white',
          }}
        >
          <Typography.Text className="fs-14-bold">
            {t(
              'result.finance.incomeBreakdown.tables.total.grossProfitFromSale.title'
            )}
          </Typography.Text>
          <Tooltip
            title={t(
              'result.finance.incomeBreakdown.tables.total.grossProfitFromSale.title'
            )}
            description={t(
              'result.finance.incomeBreakdown.tables.total.grossProfitFromSale.description'
            )}
          >
            <IconInfo fill="black" />
          </Tooltip>
        </Flex>
      ),
      beforeStorage:
        `${currencySymbol} ` +
        formatNumberWithCommas(
          Number(simulationData.result.grossProfitFromSaleWos.toFixed(0))
        ),
      afterStorage:
        `${currencySymbol} ` +
        formatNumberWithCommas(
          Number(simulationData.result.grossProfitFromSale?.toFixed(0) || 0)
        ),
      storage:
        `${currencySymbol} ` +
        formatNumberWithCommas(
          Number(simulationData.result.grossProfitOfStorageFromSale.toFixed(0))
        ),
    },
    {
      key: '2',
      title: (
        <Flex
          justify="space-between"
          align="center"
          style={{
            padding: '17px 24px',
            backgroundColor: theme.colors.blueLightest,
            borderTop: '3px solid white',
          }}
        >
          <Typography.Text className="fs-14-bold">
            {t(
              'result.finance.incomeBreakdown.tables.total.grossProfitFromCapacityReserved.title'
            )}
          </Typography.Text>
          <Tooltip
            title={t(
              'result.finance.incomeBreakdown.tables.total.grossProfitFromCapacityReserved.title'
            )}
            description={t(
              'result.finance.incomeBreakdown.tables.total.grossProfitFromCapacityReserved.description'
            )}
          >
            <IconInfo fill="black" />
          </Tooltip>
        </Flex>
      ),
      beforeStorage: `${currencySymbol} 0`,
      afterStorage:
        `${currencySymbol} ` +
        formatNumberWithCommas(
          Number(simulationData.result.revenueFromOfferedCapacity.toFixed(0))
        ),
      storage:
        `${currencySymbol} ` +
        formatNumberWithCommas(
          Number(simulationData.result.revenueFromOfferedCapacity.toFixed(0))
        ),
    },
    {
      key: '3',
      title: (
        <Flex
          justify="space-between"
          align="center"
          style={{
            padding: '17px 24px',
            backgroundColor: theme.colors.blueLightest,
            borderTop: '3px solid white',
          }}
        >
          <Typography.Text className="fs-14-bold">
            {t(
              'result.finance.incomeBreakdown.tables.total.costSavingsFromPlantAndStorage.title'
            )}
          </Typography.Text>
          <Tooltip
            title={t(
              'result.finance.incomeBreakdown.tables.total.costSavingsFromPlantAndStorage.title'
            )}
            description={t(
              'result.finance.incomeBreakdown.tables.total.costSavingsFromPlantAndStorage.description'
            )}
          >
            <IconInfo fill="black" />
          </Tooltip>
        </Flex>
      ),
      beforeStorage:
        `${currencySymbol} ` +
        formatNumberWithCommas(
          Number(simulationData.result.costSavingsFromPlant.toFixed(0))
        ),
      afterStorage:
        `${currencySymbol} ` +
        formatNumberWithCommas(
          Number(
            simulationData.result.costSavingsFromPlantAndStorage.toFixed(0)
          )
        ),
      storage:
        `${currencySymbol} ` +
        formatNumberWithCommas(
          Number(simulationData.result.costSavingsFromStorage.toFixed(0))
        ),
    },
    {
      key: '4',
      title: (
        <Flex
          justify="space-between"
          align="center"
          style={{
            padding: '17px 24px',
            backgroundColor: theme.colors.blueLightest,
            borderTop: '3px solid white',
          }}
        >
          <Typography.Text className="fs-14-bold">
            {t(
              'result.finance.incomeBreakdown.tables.total.totalGrossProfit.title'
            )}
          </Typography.Text>
          <Tooltip
            title={t(
              'result.finance.incomeBreakdown.tables.total.totalGrossProfit.title'
            )}
            description={t(
              'result.finance.incomeBreakdown.tables.total.totalGrossProfit.description'
            )}
          >
            <IconInfo fill="black" />
          </Tooltip>
        </Flex>
      ),
      beforeStorage:
        `${currencySymbol} ` +
        formatNumberWithCommas(
          Number(simulationData.result.grossProfitWos.toFixed(0))
        ),
      afterStorage:
        `${currencySymbol} ` +
        formatNumberWithCommas(
          Number(simulationData.result.grossProfit.toFixed(0))
        ),
      storage:
        `${currencySymbol} ` +
        formatNumberWithCommas(
          Number(simulationData.result.grossProfitStorage.toFixed(0))
        ),
    },
  ];

  const totalColumns = [
    {
      dataIndex: 'title',
      width: '28%',
    },
    {
      title: (
        <Flex justify="center" align="center" gap={8}>
          <Typography.Text className="fs-14-bold">
            {t('result.finance.incomeBreakdown.tables.columns.beforeStorage')}
          </Typography.Text>
        </Flex>
      ),
      dataIndex: 'beforeStorage',
      key: 'beforeStorage',
      width: '24%',
    },
    {
      title: (
        <Flex justify="center" align="center" gap={8}>
          <Typography.Text className="fs-14-bold">
            {t('result.finance.incomeBreakdown.tables.columns.afterStorage')}
          </Typography.Text>
        </Flex>
      ),
      dataIndex: 'afterStorage',
      key: 'afterStorage',
      width: '24%',
    },
    {
      title: (
        <Flex justify="center" align="center" gap={8}>
          <Typography.Text className="fs-14-bold">
            {t('result.finance.incomeBreakdown.tables.columns.storage')}
          </Typography.Text>
        </Flex>
      ),
      dataIndex: 'storage',
      key: 'storage',
      width: '24%',
    },
  ];

  return (
    <StyledTable>
      <Table
        dataSource={totalDataTable}
        columns={totalColumns}
        pagination={false}
      />
    </StyledTable>
  );
};

export default TotalTable;
